import React, { useState } from 'react';
import axios from 'axios';
import { Plus } from 'lucide-react';
import { toast } from 'sonner';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';

const API = process.env.REACT_APP_BACKEND_URL;

export const ListField = ({ label, testid, value, onChange, placeholder, hint }) => {
  const [input, setInput] = useState('');
  const add = () => {
    if (!input.trim()) return;
    onChange([...(value || []), input.trim()]);
    setInput('');
  };
  return (
    <div>
      <Label>{label}</Label>
      {hint && <p className="text-[11px] text-muted-foreground mb-1">{hint}</p>}
      <div className="flex gap-2 mt-1">
        <Input data-testid={`${testid}-input`} value={input} onChange={(e) => setInput(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), add())}
          placeholder={placeholder} className="flex-1" />
        <Button type="button" variant="outline" onClick={add} data-testid={`${testid}-add-btn`}><Plus className="w-4 h-4" /></Button>
      </div>
      {value?.length > 0 && (
        <div className="flex flex-wrap gap-1 mt-2">
          {value.map((item, i) => (
            <Badge key={`${item}-${i}`} variant="secondary" className="cursor-pointer text-[11px]"
              onClick={() => onChange(value.filter((_, idx) => idx !== i))}>
              {item} ×
            </Badge>
          ))}
        </div>
      )}
    </div>
  );
};

export const FaqField = ({ faqs, onChange }) => {
  const [q, setQ] = useState('');
  const [a, setA] = useState('');
  const add = () => {
    if (!q.trim() || !a.trim()) return;
    onChange([...(faqs || []), { q: q.trim(), a: a.trim() }]);
    setQ(''); setA('');
  };
  return (
    <div>
      <Label>Preguntas frecuentes (FAQs)</Label>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-2 mt-1">
        <Input data-testid="faq-q-input" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Pregunta" />
        <div className="flex gap-2">
          <Input data-testid="faq-a-input" value={a} onChange={(e) => setA(e.target.value)} placeholder="Respuesta" className="flex-1" />
          <Button type="button" variant="outline" onClick={add} data-testid="faq-add-btn"><Plus className="w-4 h-4" /></Button>
        </div>
      </div>
      {faqs?.length > 0 && (
        <div className="space-y-1 mt-2">
          {faqs.map((f, i) => (
            <div key={`faq-${i}`} className="flex items-start gap-2 p-2 rounded bg-muted/40 text-xs" data-testid={`faq-item-${i}`}>
              <div className="flex-1"><b>P:</b> {f.q} <br/><b>R:</b> {f.a}</div>
              <button onClick={() => onChange(faqs.filter((_, idx) => idx !== i))} className="text-red-600 dark:text-red-400">×</button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export const KnowledgeBaseManager = ({ campaignId }) => {
  const [docs, setDocs] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);
  const fileRef = React.useRef(null);

  React.useEffect(() => {
    let cancelled = false;
    axios.get(`${API}/api/campaigns/${campaignId}/knowledge`)
      .then(({ data }) => { if (!cancelled) setDocs(data || []); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [campaignId, reloadKey]);

  const onFileChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    const fd = new FormData();
    fd.append('file', file);
    fd.append('title', file.name);
    try {
      await axios.post(`${API}/api/campaigns/${campaignId}/knowledge`, fd);
      toast.success(`"${file.name}" añadido a la base de conocimiento`);
      setReloadKey(k => k + 1);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al subir');
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = '';
    }
  };

  const remove = async (id) => {
    try {
      await axios.delete(`${API}/api/campaigns/${campaignId}/knowledge/${id}`);
      toast.success('Documento eliminado');
      setReloadKey(k => k + 1);
    } catch { toast.error('Error'); }
  };

  return (
    <div className="rounded-2xl border border-brand-green/30 bg-brand-green/5 p-3 space-y-2" data-testid="knowledge-base-manager">
      <div className="flex items-center gap-2">
        <span className="text-sm font-semibold">Base de conocimiento de Noah</span>
        <span className="text-[10px] text-muted-foreground">(PDF, DOCX, XLSX, TXT, CSV)</span>
      </div>
      <div className="flex items-center gap-2">
        <input ref={fileRef} type="file" accept=".pdf,.docx,.xlsx,.xlsm,.txt,.csv,.md" onChange={onFileChange}
          className="text-xs flex-1" disabled={uploading} data-testid="knowledge-upload-input" />
        {uploading && <span className="text-xs text-muted-foreground">Subiendo...</span>}
      </div>
      {docs.length > 0 && (
        <div className="space-y-1">
          {docs.map(d => (
            <div key={d.id} className="flex items-center gap-2 text-xs p-1.5 rounded bg-muted/30" data-testid={`kb-doc-${d.id}`}>
              <span className="flex-1 truncate">{d.title || d.filename}</span>
              <span className="text-[10px] text-muted-foreground">{Math.round((d.size_bytes || 0)/1024)} KB · {d.char_count} chars</span>
              <button onClick={() => remove(d.id)} className="text-red-600 dark:text-red-400 hover:text-red-700 text-lg leading-none" data-testid={`kb-remove-${d.id}`}>×</button>
            </div>
          ))}
        </div>
      )}
      {docs.length === 0 && (
        <p className="text-[11px] text-muted-foreground italic">Aún no hay documentos. Sube manuales, catálogos, políticas o cualquier info que Noah deba conocer.</p>
      )}
    </div>
  );
};
